import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmail, signUpWithEmail, resetPassword, myRestaurantId } from "@/lib/auth";
import { firebaseAuth } from "@/lib/firebase";

/** Resolve the post-login destination from ?redirect= (internal /dashboard only). */
function redirectDest(): string {
  const r = new URLSearchParams(window.location.search).get("redirect");
  return r && r.startsWith("/dashboard") ? r : "/dashboard";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");

  // Already signed in (e.g. arriving from the POS "Upgrade" link)? Skip the
  // form and go straight to their destination / account.
  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((u) => {
      if (u) navigate(redirectDest(), { replace: true });
    });
    return unsub;
  }, [navigate]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const switchMode = (m: "signin" | "signup") => {
    setMode(m);
    setError("");
    setInfo("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    if (mode === "signup") {
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      if (password !== confirmPwd) {
        setError("Passwords do not match.");
        return;
      }
    }

    try {
      setIsLoading(true);
      if (mode === "signup") {
        await signUpWithEmail(email.trim(), password, name.trim() || undefined);
        navigate("/onboarding"); // account ready → set up the restaurant
        return;
      }
      await signInWithEmail(email.trim(), password);
      const rid = await myRestaurantId();
      // Honour a ?redirect= target (e.g. /dashboard/billing from the POS upgrade
      // button); onboard first if there's no restaurant yet.
      navigate(rid ? redirectDest() : "/onboarding");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setInfo("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter your email above first, then click 'Forgot password'.");
      return;
    }
    try {
      setIsLoading(true);
      await resetPassword(email.trim());
      setInfo("Password reset email sent. Check your inbox.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the reset email.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="theme-mkt min-h-screen flex bg-background-50">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/images/login-bg.jpg')",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/80 to-primary-500/40"></div>
        <div className="relative z-10 flex flex-col justify-end p-12">
          <blockquote className="text-background-50 text-lg leading-relaxed mb-4 italic">
            &ldquo;EzyDinePro transformed how we run our restaurant. The QR
            ordering alone increased our table turnover by 30%.&rdquo;
          </blockquote>
          <div>
            <p className="text-background-50 font-semibold text-sm">
              Gopi Sakruthi
            </p>
            <p className="text-background-200 text-xs">
              Owner, Eagle Food Court — Hyderabad
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 md:px-8">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="flex items-center gap-2 text-xl font-heading font-bold text-foreground-950 mb-10 whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img src="/images/logo.png" alt="EzyDinePro" className="h-20 md:h-24 w-auto" />
          </Link>

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            One email &amp; password for everything — dashboard, POS and Captain apps.
          </p>

          {/* Sign in / Sign up toggle */}
          <div className="flex gap-1 p-1 mb-6 bg-background-100 border border-background-200 rounded-lg">
            <button
              type="button"
              onClick={() => switchMode("signin")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                mode === "signin"
                  ? "bg-background-50 text-primary-700 shadow-sm"
                  : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => switchMode("signup")}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                mode === "signup"
                  ? "bg-background-50 text-primary-700 shadow-sm"
                  : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "signup" && (
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Your name
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                    <i className="ri-user-line"></i>
                  </span>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Rajesh Hegde"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}
            {info && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm">
                <i className="ri-checkbox-circle-line"></i>
                {info}
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-foreground-700 mb-1.5"
              >
                Email address
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-mail-line"></i>
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-10 pr-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground-700"
                >
                  Password
                </label>
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer disabled:opacity-50"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-lock-2-line"></i>
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signin" ? "Your password" : "Min. 6 characters"}
                  required
                  className="w-full pl-10 pr-11 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {mode === "signup" && (
              <div>
                <label
                  htmlFor="confirm"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  Confirm password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                    <i className="ri-lock-2-line"></i>
                  </span>
                  <input
                    id="confirm"
                    name="confirm"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    placeholder="Repeat password"
                    required
                    className="w-full pl-10 pr-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {mode === "signin" ? "Signing in..." : "Creating account..."}
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line"></i>
                  {mode === "signin" ? "Sign In" : "Create Account & Continue"}
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-foreground-500 mt-6">
            {mode === "signin" ? (
              <>
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  Start 7-Day Free Trial
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
